import type { Message, Session } from "@lr-notitia/notitia-alternis/schemas";
import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import { api } from "@lr-notitia/notitia-operator/client";
import { api as alternisAPI, apiConfig as alternisApiConfig } from "@lr-notitia/notitia-alternis/client";
import { useAudioPlayerStore } from "./audioPlayer";
import { useConfigStore } from "./config";
import { useDomainStore } from "../domain";
import { useQuestionsStore } from "../questions";
import { useGptSuggestionsStore } from "../gpt/suggestions";
// import { useGptQuestionsSchemaSpecializationStore } from "../gpt/questionsSchemaSpecialization";
import { useInstanceWorkspacesStore } from "../i-workspace";
import { useGraphStore } from "../graph";

let domainStore: ReturnType<typeof useDomainStore>;
let questionsStore: ReturnType<typeof useQuestionsStore>;
let gptSuggestionsStore: ReturnType<typeof useGptSuggestionsStore>;
// let gptQuestionsSchemaSpecializationStore: ReturnType<typeof useGptQuestionsSchemaSpecializationStore>;
let instanceWorkspaceStore: ReturnType<typeof useInstanceWorkspacesStore>;
let graphStore: ReturnType<typeof useGraphStore>;

function init() {
	domainStore = useDomainStore();
	questionsStore = useQuestionsStore();
	gptSuggestionsStore = useGptSuggestionsStore();
	// gptQuestionsSchemaSpecializationStore = useGptQuestionsSchemaSpecializationStore();
	instanceWorkspaceStore = useInstanceWorkspacesStore();
	graphStore = useGraphStore();
}

export const useSessionStore = defineStore("alternis-session", () => {
	const audioPlayerStore = useAudioPlayerStore();
	const configStore = useConfigStore();

	const sessionId = ref<string | null>(null);
	const session = ref<Session | null>(null);
	const users = computed(() => {
		let userList = session.value?.users ?? [];
		if (userList.length == 0) return userList;
		userList = userList.filter((u) => session.value?.messages.find((m) => m.userId == u.userId)).reverse();
		return userList;
	});
	let sessionSocket: WebSocket | null = null;

	const alternisMicrophoneUsed = ref(false);

	async function terminateCall(sessionId: string): Promise<void> {
		return alternisAPI().terminateCall().post(sessionId);
	}

	async function getSessions(): Promise<string[]> {
		return alternisAPI().sessions().get();
	}

	async function createSession(sessionId: string | undefined = undefined): Promise<Session> {
		return new Promise((resolve, reject) => {
			alternisAPI()
				.session()
				.post(sessionId)
				.then((s) => {
					resolve(s);
				});
			// .catch((err) => reject(err));
		});
	}

	async function loadSession(id: string): Promise<Session> {
		return new Promise((resolve, reject) => {
			alternisAPI()
				.session()
				.get(id)
				.then(async (s) => {
					sessionId.value = id;
					session.value = s;
					// wait for the socket to be open so that callers (e.g. joining a bot
					// right after loading) don't emit messages before we can receive them
					await join(id).catch((err) => console.error("Failed to join session socket", err));
					resolve(s);
				})
				.catch((err) => reject(err));
		});
	}

	function clear() {
		sessionId.value = null;
		session.value = null;
		sessionSocket?.close();
		sessionSocket = null;
		alternisMicrophoneUsed.value = false;
	}

	function join(id: string): Promise<void> {
		if (!domainStore) init();
		return new Promise((resolve, reject) => {
			if (sessionSocket) {
				return resolve();
			}
			const url = new URL(alternisApiConfig.host || window.location.href);
			const sessionSocketProtocol = url.protocol == "http:" ? "ws" : "wss";
			const sessionSocketHost = `${sessionSocketProtocol}://${url.host}`;
			sessionSocket = new WebSocket(
				`${sessionSocketHost}/api/v1/session?sessionId=${id}&userId=${domainStore.account.id}`,
			);
			sessionSocket.onopen = () => {
				api().domain(domainStore.domain.id).workspace(id).reasoning().post({
					key: "workspaceId",
					value: id,
				});
				// Resync: the socket only delivers incremental events, so anything emitted
				// between the initial session GET and the socket opening (e.g. a bot
				// "joined" system message) would be lost forever without a re-fetch.
				alternisAPI()
					.session()
					.get(id)
					.then((s) => {
						session.value = s;
					})
					.catch((err) => console.error("Failed to resync session", err));
				resolve();
			};
			sessionSocket.onerror = (err) => reject(err);
			sessionSocket.onmessage = async (event) => {
				if (!session.value) return;
				const data = JSON.parse(event.data);
				switch (data.type) {
					case "session":
						if (!data.session) throw new Error("Missing session");
						session.value = data.session;
						break;
					case "message":
						if (!data.message) throw new Error("Missing message");
						if (data.message.type === "bot") appendBotMessage(data.message);
						else appendMessage(data.message);
						break;
					case "delete":
						if (!data.messageId) throw new Error("Missing messageId");
						session.value.messages = session.value.messages.filter(
							(msg) => msg.messageId !== data.messageId,
						);
						break;
					case "tts":
						if (data.playbackType == "synthesis" && !configStore.playSynth) return;
						if (data.playbackType == "translation" && !configStore.playTranslation) return;
						if (data.playbackType == "translation-playback" && !configStore.playTranslationPlayback) return;

						let correspondingMessage = await waitForMessage(data.audioId);
						if (!correspondingMessage) return;
						if (correspondingMessage.type === "bot") {
							handleBotTTS(data);
							return;
						}
						if (correspondingMessage.type === "system") return;
						audioPlayerStore.addToQueue(
							data.audioId,
							data.playbackType == "translation-playback" ? 0.3 : 1,
						);
						break;
					default:
						console.warn("Unknown message type", data.type, data);
				}
			};
		});
	}

	function getUserLang(): string {
		// get all messages of this user, then pick the last one and get the lang
		const userMessages = session.value?.messages.filter((m) => m.userId === domainStore.account.id);
		const lastUserMessage = userMessages?.[userMessages.length - 1];
		return lastUserMessage?.lang || domainStore.languagePreference || "de";
	}

	function suggestions(lastMessage: Message) {
		if (!graphStore) init();
		if (lastMessage.userId === domainStore.account.id) return; // no suggestions for our own messages

		let chatMessages: Message[] | undefined = session.value?.messages.filter((m) => m.type !== "system").slice(-20);
		if (!chatMessages) return;
		if (chatMessages.length < 1) return;

		const templateWorkspace = graphStore.template.workspace.byId(
			instanceWorkspaceStore.openedWorkspace?.template || "",
		);
		let questions = questionsStore.unansweredQuestions;
		questions.forEach((q) => {
			if (q.useSuggestions === "false") return; // individual setting
			if (templateWorkspace?.context?.useSuggestions === false && q.useSuggestions !== "true") return; // global setting

			if (!chatMessages) return;
			if (chatMessages.length < 1) return;
			gptSuggestionsStore.askLLM(chatMessages, q);
		});
	}

	watch(
		() => session.value?.messages.length,
		async () => {
			// if (!gptQuestionsSchemaSpecializationStore) init();
			const lastMessageIndex = (session.value?.messages.length || 0) - 1;
			let lastMessage = session.value?.messages[lastMessageIndex];
			if (!lastMessage) return;
			if (lastMessage.type === "system") return;

			// ensure that message is fully processed
			if (lastMessage.isProcessing) lastMessage = await waitForTranscriptionToEnd(lastMessageIndex);
			lastMessage = await waitForLangAndWordsToBeFilled(lastMessageIndex);
			if (getUserLang() && lastMessage.lang !== getUserLang()) {
				lastMessage = await waitForTranslationToBeFilled(lastMessageIndex);
			}

			writeAlternisMsgsToDataStore();

			suggestions(lastMessage);

			// --- Temporary Quick Hack
			// gptQuestionsSchemaSpecializationStore.questionSchemaSpecialization(lastMessage);
		},
	);

	async function waitForMessage(audioId: string): Promise<Message> {
		return new Promise((resolve, reject) => {
			let maxTries = 100;
			const interval = setInterval(async () => {
				if (maxTries-- < 0) {
					clearInterval(interval);
					reject("Message not found");
				}
				let msg = session.value?.messages.find(
					(m) => m.audioId === audioId || Object.values(m.translation).find((t) => t.audioId === audioId),
				);
				if (msg) {
					clearInterval(interval);
					resolve(msg);
				}
			}, 100);
		});
	}

	// jnhck 06.08.24 - raising maxtries to 300 temporarily
	async function waitForTranscriptionToEnd(msgIndex: number): Promise<Message> {
		return new Promise((resolve, reject) => {
			let maxTries = 300;
			const interval = setInterval(async () => {
				if (maxTries-- < 0) {
					clearInterval(interval);
					reject("Transcription not ended");
				}
				let msg = session.value?.messages[msgIndex];
				if (!msg) return reject("No message found");
				if (!msg.isProcessing) {
					clearInterval(interval);
					resolve(msg);
				}
			}, 100);
		});
	}

	async function waitForLangAndWordsToBeFilled(msgIndex: number): Promise<Message> {
		return new Promise((resolve, reject) => {
			let maxTries = 100;
			const interval = setInterval(async () => {
				if (maxTries-- < 0) {
					clearInterval(interval);
					reject("Lang and words not filled");
				}
				let msg = session.value?.messages[msgIndex];
				if (!msg) return reject("No message found");
				if (msg.words.length > 0) {
					clearInterval(interval);
					resolve(msg);
				}
			}, 100);
		});
	}

	async function waitForTranslationToBeFilled(msgIndex: number, lang?: string): Promise<Message> {
		return new Promise((resolve, reject) => {
			let maxTries = 100;
			if (!lang) lang = getUserLang();
			if (!lang) return reject("No user lang found");
			const interval = setInterval(async () => {
				if (maxTries-- < 0) {
					clearInterval(interval);
					reject("Translation not filled");
				}
				let msg = session.value?.messages[msgIndex];
				if (!msg) return reject("No message found");
				if (msg.translation && msg.translation[lang!]) {
					clearInterval(interval);
					resolve(msg);
				}
			}, 100);
		});
	}

	function writeAlternisMsgsToDataStore() {
		const filteredMessages = session.value?.messages.filter((m) => m.type !== "system");
		if (!filteredMessages) return;

		if (instanceWorkspaceStore.openedWorkspaceId) {
			api().domain(domainStore.domain.id).workspace(instanceWorkspaceStore.openedWorkspaceId).reasoning().post({
				key: "alternisMessages",
				value: filteredMessages,
			});
		}
	}

	function handleBotTTS(data: any) {
		if (!session.value) return;
		let delay = 0;
		if (data.playbackType === "synthesis" || data.playbackType === "translation") delay = 1000;
		setTimeout(() => {
			audioPlayerStore.addToQueue(data.audioId, data.playbackType == "translation" ? 0.3 : 1);
		}, delay);
	}

	function appendMessage(message: Message) {
		if (!session.value) return;
		if (!message.lang) message.lang = getUserLang();
		session.value.messages = [
			...session.value.messages.filter((msg) => msg.messageId !== message.messageId),
			message,
		];
	}

	// EXPERIMENTAL
	// Bot messages are shown too fast (faster than own translation arrives)
	// so we need to wait for the translation to be filled before adding the bot message
	async function appendBotMessage(botMessage: Message) {
		if (!session.value) return;
		let lastMessageIndex = session.value.messages.length - 1;
		let lastMessage = session.value.messages[lastMessageIndex];
		if (!lastMessage) return appendMessage(botMessage);
		if (!lastMessage.lang) lastMessage.lang = getUserLang();
		// different langs & already a bot message gotten before
		let previousBotMessage = session.value.messages.find((m) => m.type === "bot");
		if (!botMessage.lang) {
			botMessage.lang = previousBotMessage?.lang || getUserLang();
		}
		let expectTranslation: boolean =
			session.value.messages.length > 3 && lastMessage.lang !== botMessage.lang && !!previousBotMessage;
		if (!expectTranslation) return appendMessage(botMessage);
		await waitForTranslationToBeFilled(lastMessageIndex, botMessage.lang);
		setTimeout(() => appendMessage(botMessage), 1500);
	}

	return {
		sessionId,
		session,
		users,
		alternisMicrophoneUsed,
		terminateCall,
		getSessions,
		createSession,
		loadSession,
		clear,
	};
});
